import * as React from 'react';
import { makeStyles, createStyles, Typography, ButtonBase, Button, Box, Divider } from '@material-ui/core';
import cn from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { RadioButtonUnchecked as RadioButtonUncheckedIcon } from '@material-ui/icons/';
import { IdentityConfirmationOption, IdentityConfirmationOptionType, BankType } from '../../generated/graphql';
import { getEntityLabel } from '../../utils/financialEntitiesList';
import { resolveBrandCountry } from '../../branding/common/utils/resolveBrandCountry';
import tranquiTheme, { TranquiTheme } from '../App/theme';
import RenderText from '../../dictionaries/RenderText';
import { BackBtnIcon } from '../../assets/icons/BackBtnIcon';
import { IdentificationConfirmationStyles } from './IdentificationConfirmationStyles';
import WaBtn from '../../components/GeneralComponents/WaBtn';
import useDictionary from '../../dictionaries/context/useDictionary';

interface IdentificationConfirmationOptionSelectionProps {
  baseClasses: IdentificationConfirmationStyles;
  id?: string | number;
  parentUrl: string;
  identityOptions: IdentityConfirmationOption[];
  bank?: BankType;
}

const getNextUrl = (option: IdentityConfirmationOption): string => {
  switch (option.type) {
    case IdentityConfirmationOptionType.Cellphone:
      return `/verificar-registrado/${option.value}`;
    case IdentityConfirmationOptionType.CcNumber:
      return '/verificar-tarjeta';
    case IdentityConfirmationOptionType.Email:
      return `/verificar-email/${option.value}`;
    default:
      return '';
  }
};

const getOptionText = (option: IdentityConfirmationOption) => {
  const { brand } = resolveBrandCountry();
  switch (option.type) {
    case IdentityConfirmationOptionType.Cellphone:
      return <RenderText textKey="validation_options_phone" context={{ phone: option.value! }} />;
    case IdentityConfirmationOptionType.CcNumber:
      return <RenderText textKey="validation_options_tc" context={{ bank: getEntityLabel(option.bank!, brand) }} />;
    case IdentityConfirmationOptionType.Email:
      return <RenderText textKey="validation_options_email" context={{ correo: option.value! }} />;
    default:
      return '';
  }
};

const IdentificationConfirmationOptionSelection:
React.FC<IdentificationConfirmationOptionSelectionProps> = (
  { baseClasses, parentUrl, identityOptions, id, bank },
) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{ deepLinkFailed: boolean }>();
  const { gT } = useDictionary();

  const { deepLinkFailed = false } = location.state || {};

  const maxOptions = 3;
  const [ optionsOpened, setOptionsOpened ] = React.useState(false);
  const handleOptionsOpen = () => setOptionsOpened(true);

  const options = identityOptions.slice(0, optionsOpened ? undefined : maxOptions);

  const getHandleContinue = (option: IdentityConfirmationOption) => () => {
    history.push(`${parentUrl}${getNextUrl(option)}`);
  };

  const titleRenderKey = deepLinkFailed ? 'validation_options_title_no_match' : 'validation_options_title';
  const descriptionRenderKey = deepLinkFailed ? 'validation_options_description_no_match' : 'validation_options_description';

  return (
    <>
      {identityOptions.length === 0 ? (
        <>
          <Typography className={cn(baseClasses.title, 'mb-2')}>
            <RenderText textKey="validation_no_attempts_title" />
          </Typography>
          <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
            <RenderText textKey="validation_no_attempts_description" />
          </Typography>
          <WaBtn
            msg={gT('validation_no_attempts_wa_message')}
            title="¡ESCRÍBENOS!"
            bank={bank}
          />
        </>
      ) : (
        <>
          <Typography className={cn(baseClasses.title, 'mb-2')}>
            <RenderText textKey={titleRenderKey} />
          </Typography>

          {
            !deepLinkFailed ? (
              <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
                <RenderText isMarkdown textKey="validation_options_document" context={{ document: id || '' }} />
              </Typography>
            ) : null
          }

          <Typography className={cn(baseClasses.text, 'mt-2 mb-3')}>
            <RenderText isMarkdown textKey={descriptionRenderKey} context={{ document: id || '' }} />
          </Typography>

          <Box className={`${!optionsOpened && 'mb-2'}`} data-testid="identificationOptions">
            {options.map((obj, index) => {
              return (
                <Box key={obj.value || obj.type}>
                  <ButtonBase
                    className={cn(classes.item)}
                    onClick={getHandleContinue(obj)}
                    id={obj.value || obj.type}
                    data-testid="identification-confirmation-option-selection__id-validation-option"
                  >
                    <RadioButtonUncheckedIcon className={cn(classes.rabiobtn)} />
                    <Typography className={classes.itemText}>
                      { getOptionText(obj) }
                    </Typography>
                  </ButtonBase>
                  {index !== options.length - 1 && <Divider className="my-1" />}
                </Box>
              );
            })}
          </Box>
          <Box display="flex" className="mt-2">
            {!optionsOpened && identityOptions.length > maxOptions && (
              <Button
                onClick={handleOptionsOpen}
                variant="text"
                color="primary"
                size="small"
                className={cn(classes.moreOptions)}
                data-testid="identification-confirmation-option-selection__more-options-button"
              >
                <RenderText textKey="validation_options_more_options_button" />
                <BackBtnIcon className="btnIcon" color={tranquiTheme.palette.text3.main} />
              </Button>
            )}
          </Box>
        </>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  btnWrappers: {
    display: ' flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4),
    },
  },
  item: {
    transition: 'all .4s',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0),
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    wordBreak: 'break-word',
    '&:hover': {
      backgroundColor: theme.palette.bg3.default,
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1, 2),
    },
  },
  itemText: {
    ...theme.typography.body2,
    color: theme.palette.text2.main,
    textAlign: 'left',
  },
  moreOptions: {
    fontWeight: 700,
    color: theme.palette.text3.main,
    textTransform: 'none',
    margin: '0 auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .btnIcon': {
      transform: 'rotate(-90deg)',
      marginLeft: theme.spacing(2),
    },
  },
  rabiobtn: {
    color: theme.palette.text4.main,
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(1.5),
    },
  },
}));

export default IdentificationConfirmationOptionSelection;
